import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { fetchProgramList } from '../redux/actions/Actions'
import RequestCallback from '../RequestCallback'
import axiosinstance from '../../api/axiosinstance'
import JourneyWorkflow from './JourneyWorkflow'
import ProgramDurationSection from './ProgramDurationSection'
import SEO from '../common/SEO'
import IncludedCoursesSection from './IncludedCoursesSection'
import ProgramHighlightsSection from './ProgramHighlightsSection'
import LiveProjectsSection from './LiveProjectsSection'
import { MonitorIcon, CodeIcon, TargetIcon, ChatIcon, UsersIcon, DollarIcon, TrendingUpIcon } from './programHighlightIcons'
import { FaJava, FaPython, FaReact, FaAngular, FaNodeJs, FaAws, FaDocker, FaGitAlt, FaJenkins, FaFigma } from 'react-icons/fa'
import { SiJavascript, SiDotnet, SiSpringboot, SiMysql, SiMongodb, SiPostman, SiJira } from 'react-icons/si'

const DFT_LIVE_FEATURES = [
  'Work on Live Client Projects',
  'Real Company Environment',
  'Agile Scrum Process',
  'Daily Stand-ups & Sprint Planning',
  'Code Reviews by Senior Developers',
  'Client Presentations & Demos',
  'Production Deployment',
];

const DFT_LIVE_PROJECTS = [
  { name:'CRM Application',            icon:'👥' },
  { name:'HRMS Application',           icon:'💼' },
  { name:'Hospital Management System', icon:'🏥' },
  { name:'E-Commerce Platform',        icon:'🛒' },
  { name:'LMS Portal',                 icon:'📚' },
  { name:'ERP System',                 icon:'⚙️' },
];

const DFT_HIGHLIGHTS = [
  { label: 'Industry-Aligned Training',          icon: <MonitorIcon /> },
  { label: 'Work on Live Projects',              icon: <CodeIcon /> },
  { label: 'Agile Scrum Environment',            icon: <TargetIcon /> },
  { label: 'Client Interaction & Communication', icon: <ChatIcon /> },
  { label: 'Mentor & Peer Collaboration',         icon: <UsersIcon /> },
  { label: 'Stipend Opportunity (if available)', icon: <DollarIcon /> },
  { label: 'Career Growth & Full-time Roles',    icon: <TrendingUpIcon /> },
]

const CHECK = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
)
const ARROW = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
)

const DFT_JOURNEY_STEPS = [
  { label: 'Enrollment',           icon: 'enroll',      color: '#16A34A' },
  { label: 'Technical Training',   icon: 'training',    color: '#16A34A' },
  { label: 'Skill Assessments',    icon: 'assessment',  color: '#16A34A' },
  { label: 'Live Client Project',  icon: 'team',        color: '#16A34A' },
  { label: 'Agile Scrum Process',  icon: 'target',      color: '#16A34A' },
  { label: 'Code Reviews',         icon: 'review',      color: '#16A34A' },
  { label: 'Continuous Learning',  icon: 'book',        color: '#16A34A' },
  { label: 'Certification',        icon: 'certificate', color: '#16A34A' },
  { label: 'Placement Support',    icon: 'placement',   color: '#16A34A' },
];

const DFT_DURATIONS = [
  {
    title: '3 Months Program', color: '#16A34A', colorDark: '#15803D', bg: '#F0FDF4',
    phases: [
      { name: 'Training Phase', duration: '1 Month', items: ['Core Tech Stack', 'Tools & Best Practices', 'Coding Standards'] },
      { name: 'Live Project Work', duration: '1 Month', items: ['Real Client Projects', 'Agile Process', 'Daily Stand-ups'] },
      { name: 'Engagement & Growth', duration: '1 Month', items: ['Code Reviews', 'Continuous Learning', 'Deliver Value'] },
    ],
    deliverables: ['Experience Letter', 'Project Certificate', 'Performance Review'],
  },
  {
    title: '6 Months Program', color: '#2563EB', colorDark: '#1D4ED8', bg: '#EFF6FF',
    phases: [
      { name: 'Advanced Training', duration: '2 Months', items: ['Core Tech Stack', 'Advanced Tools', 'System Design Basics'] },
      { name: 'Live Project Work', duration: '2 Months', items: ['Real Client Projects', 'Agile Process', 'Cross-team Collaboration'] },
      { name: 'Leadership & Mentoring', duration: '2 Months', items: ['Code Reviews', 'Mentoring Juniors', 'Career Growth Planning'] },
    ],
    deliverables: ['Experience Certificate', 'Multiple Project Certificates', 'Career Growth Plan'],
  },
];

export default function DeveloperFullTime() {
  const navigate  = useNavigate()
  const dispatch  = useDispatch()
  const programs  = useSelector((s) => s.fetchAllPrograms.programs || [])
  const [showApply, setShowApply] = useState(null) // null | 'enroll' | 'download'

  useEffect(() => { dispatch(fetchProgramList()) }, [dispatch])

  const [courses, setCourses] = useState([])
  useEffect(() => {
    axiosinstance.get('/api/category/all/2')
      .then(res => setCourses(res.data.filter(c => c.pdf_file_path)))
      .catch(() => {})
  }, [])

  const handleApplySuccess = () => {
    if (showApply === 'download') {
      setShowApply(null)
      navigate('/download', {
        state: {
          programInterested:  'Developer Full Time Program',
          Program_id:         2,
          interestedCourse:   'JAVA WEB PROGRAMMING',
          interestedCourseId: 15,
        }
      })
    }
  }

  return (
    <div style={{ fontFamily:"'Inter','Segoe UI',sans-serif", overflowX:'hidden' }}>
      <SEO
        title="Developer Full Time Program"
        path="/programDetail/2"
        description="UPL Snipe's Developer Full Time Program — train, build, work and grow with live projects, agile scrum practices, and real-world client experience. 95%+ placement rate."
        keywords="developer training program, full time developer course, live project training, agile scrum training, software developer career, IT job placement"
      />
      <style>{`
        #dft-hero       { background: linear-gradient(135deg,#040A04 0%,#091409 50%,#060E06 100%) !important; }
        #dft-structure  { background-color: #F0FDF4 !important; }
        #dft-live       { background-color: #ffffff !important; }
        #dft-skills     { background-color: #F0FDF4 !important; }
        #dft-whychoose  { background-color: #0C2212 !important; }
        #dft-faq        { background-color: #F0FDF4 !important; }
        #dft-cta        { background-color: #071A0B !important; }

        @media (max-width: 900px) {
          #dft-hero            { min-height: auto !important; }
          #dft-hero-grid       { grid-template-columns: 1fr !important; gap: 24px !important; }
          #dft-hero-right      { display: none !important; }
          #dft-structure-grid  { grid-template-columns: 1fr !important; gap: 20px !important; }
          .dft-structure-arrow { display: none !important; }
          #dft-live-grid       { grid-template-columns: 1fr !important; }
          .dft-live-inner      { grid-template-columns: 1fr !important; gap: 24px !important; }
          .dft-agile-steps     { flex-wrap: wrap !important; justify-content: center !important; }
          .dft-agile-arrow     { display: none !important; }
          #dft-skills-grid     { grid-template-columns: 1fr !important; }
          #dft-tech-grid       { grid-template-columns: repeat(4,1fr) !important; }
          #dft-whychoose-grid  { grid-template-columns: repeat(3,1fr) !important; }
          .dft-whychoose-item:nth-child(4) { border-left: none !important; }
          #dft-faq-grid        { grid-template-columns: 1fr !important; }
          .dft-college-svg     { display: none !important; }
          #dft-cta-row         { flex-direction: column !important; text-align: center !important; }
          .dft-cta-svg         { display: none !important; }
        }
        @media (max-width: 600px) {
          .dft-2col-sm         { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          #dft-tech-grid       { grid-template-columns: repeat(3,1fr) !important; }
          #dft-whychoose-grid  { grid-template-columns: repeat(2,1fr) !important; }
          .dft-whychoose-item:nth-child(3) { border-left: none !important; }
          .dft-whychoose-item:nth-child(5) { border-left: none !important; }
        }
      `}</style>

      {/* ══ HERO ══ */}
      <section id="dft-hero" style={{ padding:'44px 4% 52px', position:'relative', overflow:'hidden', minHeight:'100vh', boxSizing:'border-box' }}>
        <div style={{ position:'absolute', top:'5%', left:'40%', width:420, height:420, borderRadius:'50%', background:'radial-gradient(circle,rgba(34,197,94,0.08) 0%,transparent 65%)', pointerEvents:'none' }}/>

        {/* Breadcrumb */}
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12.5, color:'rgba(255,255,255,0.4)', marginBottom:32, position:'relative', zIndex:2 }}>
          <Link to="/" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Home</Link>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <Link to="/programs" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>Programs</Link>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          <span style={{ color:'rgba(255,255,255,0.75)' }}>Developer Full Time Program</span>
        </div>

        <div id="dft-hero-grid" style={{ maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:40, alignItems:'center', position:'relative', zIndex:2 }}>

          {/* LEFT — text */}
          <div>
            <h1 style={{ margin:'0 0 10px', lineHeight:1.05 }}>
              <span style={{ display:'block', fontSize:50, fontWeight:900, color:'#fff', letterSpacing:'-0.01em' }}>DEVELOPER</span>
              <span style={{ display:'block', fontSize:50, fontWeight:900, letterSpacing:'-0.01em' }}>
                <span style={{ color:'#22C55E' }}>FULL TIME</span>
                <span style={{ color:'#fff' }}> PROGRAM</span>
              </span>
            </h1>
            <div style={{ fontSize:22, fontWeight:800, color:'#22C55E', margin:'0 0 16px' }}>Train. Build. Work. Grow.</div>
            <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:999, padding:'7px 20px', marginBottom:18 }}>
              {['3 Months','6 Months','Offline','Online','Hybrid'].map((t,i,a) => (
                <React.Fragment key={t}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{t}</span>
                  {i<a.length-1 && <span style={{ color:'rgba(255,255,255,0.35)', margin:'0 10px' }}>|</span>}
                </React.Fragment>
              ))}
            </div>
            <p style={{ fontSize:14, color:'rgba(255,255,255,0.65)', margin:'0 0 28px', lineHeight:1.8, maxWidth:500 }}>
              A career accelerating program that goes beyond learning — where you gain industry ready skills, collaborate on live projects, adopt agile practices, and engage directly with clients to build real world experience.
            </p>

            <div style={{ display:'flex', gap:14 }}>
              <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 30px', background:'#16A34A', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 18px rgba(22,163,74,0.5)' }}>
                Apply Now
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
              <button onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior:'smooth' })} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'13px 24px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.3)', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                View Courses
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </div>
          </div>

          {/* RIGHT — code monitor visual + floating cards */}
          <div id="dft-hero-right" style={{ position:'relative', height:420 }}>
            {/* Monitor/code visual */}
            <div style={{ position:'absolute', left:0, top:20, right:210 }}>
              <svg width="280" height="320" viewBox="0 0 280 320" fill="none">
                {/* Main monitor */}
                <rect x="10" y="30" width="200" height="145" rx="8" fill="#0D1A0D" stroke="#1E4D1E" strokeWidth="2"/>
                <rect x="17" y="37" width="186" height="128" rx="4" fill="#050D05"/>
                {/* Code lines */}
                <rect x="24" y="48" width="55" height="4" rx="2" fill="#22C55E" opacity="0.9"/>
                <rect x="24" y="57" width="90" height="4" rx="2" fill="#4ADE80" opacity="0.5"/>
                <rect x="34" y="66" width="70" height="4" rx="2" fill="#86EFAC" opacity="0.45"/>
                <rect x="24" y="75" width="50" height="4" rx="2" fill="#22C55E" opacity="0.75"/>
                <rect x="34" y="84" width="80" height="4" rx="2" fill="#4ADE80" opacity="0.5"/>
                <rect x="24" y="93" width="65" height="4" rx="2" fill="#16A34A" opacity="0.65"/>
                <rect x="34" y="102" width="45" height="4" rx="2" fill="#22C55E" opacity="0.45"/>
                <rect x="24" y="111" width="88" height="4" rx="2" fill="#4ADE80" opacity="0.55"/>
                <rect x="24" y="120" width="55" height="4" rx="2" fill="#22C55E" opacity="0.75"/>
                <rect x="34" y="129" width="72" height="4" rx="2" fill="#86EFAC" opacity="0.45"/>
                <rect x="24" y="138" width="40" height="4" rx="2" fill="#22C55E" opacity="0.6"/>
                <rect x="34" y="147" width="60" height="4" rx="2" fill="#4ADE80" opacity="0.4"/>
                {/* Cursor blink */}
                <rect x="24" y="156" width="8" height="4" rx="1" fill="#22C55E" opacity="0.9"/>
                {/* Stand */}
                <rect x="102" y="175" width="16" height="16" rx="2" fill="#1A2A1A"/>
                <rect x="88" y="189" width="44" height="6" rx="2" fill="#1A2A1A"/>
                {/* Side monitor */}
                <rect x="5" y="80" width="10" height="60" rx="3" fill="#0D1A0D" stroke="#1E4D1E" strokeWidth="1"/>
                {/* Keyboard */}
                <rect x="30" y="210" width="160" height="26" rx="4" fill="#0D1A0D" stroke="#1E4D1E" strokeWidth="1.5"/>
                <rect x="36" y="215" width="148" height="16" rx="2" fill="#050D05"/>
                {[0,1,2,3].map(r => (
                  [0,1,2,3,4,5,6,7].map(c => (
                    <rect key={`${r}-${c}`} x={40 + c*18} y={218 + r*4} width="14" height="2.5" rx="1" fill="#22C55E" opacity={0.15 + Math.random()*0.15}/>
                  ))
                ))}
                {/* Desk */}
                <rect x="0" y="238" width="280" height="10" rx="3" fill="#1A2A1A"/>
                {/* Green glow on desk */}
                <rect x="20" y="235" width="180" height="4" rx="2" fill="#22C55E" opacity="0.06"/>
              </svg>
            </div>

            {/* Floating feature cards */}
            <div style={{ position:'absolute', right:0, top:10, display:'flex', flexDirection:'column', gap:10, width:196 }}>
              {[
                { label:'Work on\nLive Projects',  icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg> },
                { label:'Follow\nAgile Scrum',      icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></svg> },
                { label:'Client\nDiscussions',      icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
                { label:'Real World\nExperience',   icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg> },
              ].map(f => (
                <div key={f.label} style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:10, padding:'10px 14px' }}>
                  <div style={{ flexShrink:0 }}>{f.icon}</div>
                  <span style={{ fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.8)', whiteSpace:'pre-line', lineHeight:1.35 }}>{f.label}</span>
                </div>
              ))}
              {/* Build your career box */}
              <div style={{ border:'2px solid #22C55E', borderRadius:10, padding:'14px 16px', marginTop:4 }}>
                <div style={{ fontSize:14, fontWeight:800, color:'#fff', lineHeight:1.5 }}>Build your career.<br/>Deliver real impact.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROGRAM HIGHLIGHTS ══ */}
      <ProgramHighlightsSection
        items={DFT_HIGHLIGHTS}
        accentColor="#16A34A"
        iconBg="#F0FDF4"
        iconBorder="#BBF7D0"
        cardBorder="#D1FAE5"
      />

      {/* ══ COURSES ══ */}
      <div id="courses-section">
        <IncludedCoursesSection courses={courses} programId={2} programName="Developer Full Time Program" />
      </div>

      {/* ══ PROGRAM DURATION ══ */}
      <ProgramDurationSection
        eyebrowColor="#16A34A" eyebrowBg="#F0FDF4" eyebrowBorder="#BBF7D0"
        titlePlain="Program" titleHighlight="Duration" titleHighlightColor="#16A34A"
        subtitle="Choose between a focused 3-month program or an extended 6-month program with deeper live-project exposure."
        durations={DFT_DURATIONS}
      />

      {/* ══ PROGRAM STRUCTURE ══ */}
      <section id="dft-structure" style={{ padding:'64px 4%' }}>
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:22, fontWeight:900, color:'#16A34A', letterSpacing:'0.06em', margin:'0 0 44px' }}>PROGRAM STRUCTURE</h2>
          <div id="dft-structure-grid" style={{ display:'grid', gridTemplateColumns:'1fr 36px 1fr 36px 1fr 36px 1fr', alignItems:'start' }}>

            {/* Phase 1 */}
            <div style={{ border:'1.5px solid #BBF7D0', borderRadius:14, padding:'24px 20px', background:'#fff' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'#16A34A', color:'#fff', fontWeight:900, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>1</div>
                <div>
                  <div style={{ fontWeight:900, fontSize:13, color:'#15803D', letterSpacing:'0.03em' }}>TRAINING PHASE</div>
                </div>
              </div>
              {['Core Technical Training','Industry Best Practices','Tools & Technologies','Coding Standards','Assessments'].map(it => (
                <div key={it} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151', marginBottom:10 }}>
                  {CHECK}{it}
                </div>
              ))}
            </div>

            <div className="dft-structure-arrow" style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:58 }}>{ARROW}</div>

            {/* Phase 2 */}
            <div style={{ border:'1.5px solid #BBF7D0', borderRadius:14, padding:'24px 20px', background:'#fff' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'#16A34A', color:'#fff', fontWeight:900, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>2</div>
                <div>
                  <div style={{ fontWeight:900, fontSize:13, color:'#15803D', letterSpacing:'0.03em' }}>LIVE PROJECT WORK</div>
                </div>
              </div>
              {['Work on Real Client Projects','Apply Concepts in Real-time','Collaborate with Team','Follow Scrum Process','Daily Stand-ups'].map(it => (
                <div key={it} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151', marginBottom:10 }}>
                  {CHECK}{it}
                </div>
              ))}
            </div>

            <div className="dft-structure-arrow" style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:58 }}>{ARROW}</div>

            {/* Phase 3 */}
            <div style={{ border:'1.5px solid #BBF7D0', borderRadius:14, padding:'24px 20px', background:'#fff' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'#16A34A', color:'#fff', fontWeight:900, fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>3</div>
                <div>
                  <div style={{ fontWeight:900, fontSize:13, color:'#15803D', letterSpacing:'0.03em' }}>ENGAGEMENT & GROWTH</div>
                </div>
              </div>
              {['Client Discussions','Requirement Understanding','Deliver Value','Code Reviews','Continuous Learning'].map(it => (
                <div key={it} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151', marginBottom:10 }}>
                  {CHECK}{it}
                </div>
              ))}
            </div>

            <div className="dft-structure-arrow" style={{ display:'flex', alignItems:'center', justifyContent:'center', paddingTop:58 }}>{ARROW}</div>

            {/* Stipend card */}
            <div style={{ background:'#15803D', borderRadius:14, padding:'28px 22px', color:'#fff', textAlign:'center', display:'flex', flexDirection:'column', alignItems:'center' }}>
              <div style={{ fontWeight:900, fontSize:15, lineHeight:1.4, marginBottom:16 }}>OPPORTUNITY TO<br/>EARN STIPEND</div>
              <div style={{ width:54, height:54, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'1.5px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:18 }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <p style={{ fontSize:13, color:'rgba(255,255,255,0.88)', lineHeight:1.7, margin:0 }}>
                You may be eligible to earn a stipend based on the client's decision and project performance.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ══ JOURNEY WORKFLOW ══ */}
      <JourneyWorkflow title="Developer Full Time Journey Workflow" steps={DFT_JOURNEY_STEPS} accentColor="#16A34A" />

      {/* ══ SKILLS & TECHNOLOGIES + CAREER OUTCOMES ══ */}
      <section id="dft-skills" style={{ padding:'56px 4%' }}>
        <div id="dft-skills-grid" style={{ maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:28 }}>

          {/* Skills & Technologies */}
          <div style={{ border:'1.5px solid #D1FAE5', borderRadius:16, padding:'32px 28px', background:'#fff' }}>
            <h3 style={{ fontSize:16, fontWeight:900, color:'#15803D', textAlign:'center', margin:'0 0 28px', letterSpacing:'0.04em' }}>TECHNOLOGY YOU WILL LEARN</h3>
            <div id="dft-tech-grid" style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:16 }}>
              {[
                { name:'Java',        color:'#E76F00', bg:'#FFF7ED', abbr:'☕', icon: FaJava },
                { name:'Python',      color:'#3776AB', bg:'#EFF6FF', abbr:'🐍', icon: FaPython },
                { name:'JavaScript',  color:'#F7DF1E', bg:'#FEFCE8', abbr:'JS', icon: SiJavascript },
                { name:'React',       color:'#61DAFB', bg:'#F0F9FF', abbr:'⚛', icon: FaReact },
                { name:'Angular',     color:'#DD0031', bg:'#FEF2F2', abbr:'A', icon: FaAngular },
                { name:'Node.js',     color:'#339933', bg:'#F0FDF4', abbr:'N', icon: FaNodeJs },
                { name:'.NET',        color:'#512BD4', bg:'#F5F3FF', abbr:'.net', icon: SiDotnet },
                { name:'Spring Boot', color:'#6DB33F', bg:'#F0FDF4', abbr:'🌿', icon: SiSpringboot },
                { name:'MySQL',       color:'#4479A1', bg:'#F0F9FF', abbr:'SQL', icon: SiMysql },
                { name:'MongoDB',     color:'#47A248', bg:'#F0FDF4', abbr:'M', icon: SiMongodb },
                { name:'AWS',         color:'#FF9900', bg:'#FFFBEB', abbr:'aws', icon: FaAws },
                { name:'Docker',      color:'#2496ED', bg:'#EFF6FF', abbr:'🐳', icon: FaDocker },
                { name:'Git',         color:'#F05032', bg:'#FFF7ED', abbr:'⎇', icon: FaGitAlt },
                { name:'Postman',     color:'#FF6C37', bg:'#FFF7ED', abbr:'📮', icon: SiPostman },
                { name:'Jenkins',     color:'#D33833', bg:'#FEF2F2', abbr:'J', icon: FaJenkins },
                { name:'Jira',        color:'#0052CC', bg:'#EFF6FF', abbr:'Jira', icon: SiJira },
                { name:'Figma',       color:'#F24E1E', bg:'#FDF2F8', abbr:'Fig', icon: FaFigma },
                { name:'and more...', color:'#6B7280', bg:'#F9FAFB', abbr:'...' },
              ].map(t => (
                <div key={t.name} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, textAlign:'center' }}>
                  <div style={{ width:46, height:46, borderRadius:10, background:t.bg, border:`1.5px solid ${t.color}30`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:t.abbr.length > 2 ? 10 : 16, fontWeight:900, color:t.color }}>
                    {t.icon ? <t.icon size={22} color={t.color} /> : t.abbr}
                  </div>
                  <span style={{ fontSize:10.5, fontWeight:600, color:'#374151', lineHeight:1.3 }}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Career Outcomes */}
          <div style={{ border:'1.5px solid #D1FAE5', borderRadius:16, padding:'32px 28px', background:'#fff' }}>
            <h3 style={{ fontSize:16, fontWeight:900, color:'#15803D', textAlign:'center', margin:'0 0 28px', letterSpacing:'0.04em' }}>CAREER OUTCOMES</h3>
            <div className="dft-2col-sm" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {[
                { title:'Real World\nExperience',           icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg> },
                { title:'Strong Portfolio\nof Live Projects', icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg> },
                { title:'Better Job\nOpportunities',         icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg> },
                { title:'Industry-Ready\nSkill Set',          icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg> },
                { title:'Fast Track Your\nCareer Growth',     icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg> },
                { title:'Full-time Roles\nin Top Companies',  icon:<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
              ].map(c => (
                <div key={c.title} style={{ display:'flex', alignItems:'center', gap:14, padding:'16px', border:'1px solid #D1FAE5', borderRadius:12 }}>
                  <div style={{ width:46, height:46, borderRadius:10, background:'#F0FDF4', border:'1px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{c.icon}</div>
                  <span style={{ fontSize:13, fontWeight:700, color:'#166534', lineHeight:1.4, whiteSpace:'pre-line' }}>{c.title}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ══ LIVE PROJECTS ══ */}
      <LiveProjectsSection
        accentColor="#16A34A"
        accentBg="#F0FDF4"
        accentBorder="#BBF7D0"
        title="Live Project Internship Experience"
        features={DFT_LIVE_FEATURES}
        projects={DFT_LIVE_PROJECTS}
      />

      {/* ══ WHY CHOOSE OUR DEVELOPER PROGRAM ══ */}
      <section id="dft-whychoose" style={{ padding:'36px 4%' }}>
        <div style={{ maxWidth:1280, margin:'0 auto' }}>
          <h2 style={{ textAlign:'center', fontSize:18, fontWeight:900, color:'#fff', letterSpacing:'0.06em', margin:'0 0 28px' }}>WHY CHOOSE OUR DEVELOPER PROGRAM?</h2>
          <div id="dft-whychoose-grid" style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:1, borderRadius:12, overflow:'hidden', border:'1px solid rgba(255,255,255,0.08)' }}>
            {[
              { title:'Training + Real Work',    sub:'Best of both worlds',               icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
              { title:'Work with Experts',        sub:'Learn from industry professionals', icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/></svg> },
              { title:'Agile Environment',        sub:'Experience real-world workflows',   icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></svg> },
              { title:'Client Exposure',          sub:'Improve communication skills',      icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
              { title:'Hands-on Learning',        sub:'Build by doing',                    icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg> },
              { title:'Growth & Recognition',     sub:'Stand out and grow faster',         icon:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg> },
            ].map((w, i) => (
              <div key={w.title} className="dft-whychoose-item" style={{ padding:'22px 16px', background:'rgba(255,255,255,0.03)', borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:9, background:'rgba(34,197,94,0.12)', border:'1px solid rgba(74,222,128,0.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{w.icon}</div>
                <div>
                  <div style={{ fontSize:13, fontWeight:800, color:'#fff', marginBottom:4, lineHeight:1.3 }}>{w.title}</div>
                  <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.5)', lineHeight:1.4 }}>{w.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ + COLLEGES ══ */}
      <section id="dft-faq" style={{ padding:'56px 4%' }}>
        <div id="dft-faq-grid" style={{ maxWidth:1280, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:28, alignItems:'start' }}>

          {/* FAQ */}
          <div>
            <h3 style={{ fontSize:17, fontWeight:900, color:'#15803D', margin:'0 0 22px', letterSpacing:'0.04em' }}>FREQUENTLY ASKED QUESTIONS</h3>
            {[
              'What is the duration of the training phase?',
              'Will I work on live projects after training?',
              'Is stipend guaranteed?',
              'Do I get a certificate?',
              'What technologies will I work on?',
            ].map(q => (
              <div key={q} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:'1.5px solid #D1FAE5', borderRadius:10, padding:'16px 20px', background:'#fff', marginBottom:10, cursor:'pointer', gap:12 }}>
                <span style={{ fontSize:14, color:'#374151', fontWeight:500 }}>{q}</span>
                <span style={{ width:26, height:26, borderRadius:'50%', border:'1.5px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#16A34A', fontSize:20, fontWeight:300, lineHeight:1 }}>+</span>
              </div>
            ))}
          </div>

          {/* Colleges */}
          <div style={{ display:'flex', alignItems:'center', gap:20, border:'1.5px solid #D1FAE5', borderRadius:16, padding:'32px 28px', background:'#fff' }}>
            <div style={{ flex:1 }}>
              <h3 style={{ fontSize:17, fontWeight:900, color:'#15803D', margin:'0 0 14px', letterSpacing:'0.02em' }}>COLLEGES / INSTITUTIONS</h3>
              <p style={{ fontSize:13.5, color:'#4B5563', lineHeight:1.75, margin:'0 0 22px' }}>
                We welcome colleges to send their enquiries for discussions on training, assessments and placement readiness programs.
              </p>
              <Link to="/contact" style={{ display:'inline-block', padding:'12px 28px', background:'#16A34A', color:'#fff', borderRadius:8, fontSize:14, fontWeight:700, textDecoration:'none', boxShadow:'0 4px 14px rgba(22,163,74,0.35)' }}>
                Send Enquiry
              </Link>
            </div>
            {/* College building SVG */}
            <div className="dft-college-svg" style={{ flexShrink:0 }}>
              <svg width="130" height="130" viewBox="0 0 130 130" fill="none">
                <ellipse cx="65" cy="118" rx="50" ry="8" fill="#BBF7D0" opacity="0.5"/>
                {/* Ground */}
                <rect x="10" y="108" width="110" height="6" rx="2" fill="#166534" opacity="0.3"/>
                {/* Steps */}
                <rect x="20" y="102" width="90" height="6" rx="1" fill="#D1FAE5"/>
                <rect x="28" y="97" width="74" height="6" rx="1" fill="#D1FAE5"/>
                {/* Main building body */}
                <rect x="30" y="42" width="70" height="56" rx="2" fill="#F0FDF4" stroke="#BBF7D0" strokeWidth="1.5"/>
                {/* Roof / pediment */}
                <polygon points="18,44 65,12 112,44" fill="#D1FAE5" stroke="#BBF7D0" strokeWidth="1.5"/>
                <polygon points="30,44 65,20 100,44" fill="#F0FDF4"/>
                {/* Flag */}
                <line x1="65" y1="12" x2="65" y2="2" stroke="#166534" strokeWidth="1.5"/>
                <polygon points="65,2 74,6 65,10" fill="#16A34A"/>
                {/* Columns */}
                {[38, 52, 66, 80, 94].map(x => (
                  <rect key={x} x={x} y="44" width="8" height="54" rx="2" fill="#D1FAE5" stroke="#BBF7D0" strokeWidth="1"/>
                ))}
                {/* Door */}
                <rect x="55" y="78" width="20" height="24" rx="3" fill="#166534" opacity="0.7"/>
                <rect x="58" y="81" width="6" height="8" rx="1" fill="#D1FAE5" opacity="0.5"/>
                <rect x="66" y="81" width="6" height="8" rx="1" fill="#D1FAE5" opacity="0.5"/>
                {/* Windows */}
                <rect x="34" y="58" width="12" height="12" rx="2" fill="#BBF7D0"/>
                <rect x="84" y="58" width="12" height="12" rx="2" fill="#BBF7D0"/>
                <line x1="40" y1="58" x2="40" y2="70" stroke="#D1FAE5" strokeWidth="1"/>
                <line x1="34" y1="64" x2="46" y2="64" stroke="#D1FAE5" strokeWidth="1"/>
                <line x1="90" y1="58" x2="90" y2="70" stroke="#D1FAE5" strokeWidth="1"/>
                <line x1="84" y1="64" x2="96" y2="64" stroke="#D1FAE5" strokeWidth="1"/>
                {/* Trees */}
                <circle cx="14" cy="98" r="10" fill="#16A34A" opacity="0.5"/>
                <rect x="13" y="105" width="3" height="8" fill="#166534" opacity="0.4"/>
                <circle cx="116" cy="98" r="10" fill="#16A34A" opacity="0.5"/>
                <rect x="115" y="105" width="3" height="8" fill="#166534" opacity="0.4"/>
              </svg>
            </div>
          </div>

        </div>
      </section>

      {/* ══ CTA BANNER ══ */}
      <section id="dft-cta" style={{ padding:'48px 4%' }}>
        <div id="dft-cta-row" style={{ maxWidth:1280, margin:'0 auto', display:'flex', alignItems:'center', gap:32 }}>

          {/* Developer character SVG */}
          <div className="dft-cta-svg" style={{ flexShrink:0, width:130, height:130, position:'relative' }}>
            <svg width="130" height="130" viewBox="0 0 130 130" fill="none">
              {/* Body */}
              <ellipse cx="65" cy="115" rx="30" ry="8" fill="rgba(34,197,94,0.15)"/>
              <rect x="38" y="72" width="54" height="50" rx="14" fill="#1A4A1A"/>
              {/* Hoodie strings */}
              <line x1="55" y1="78" x2="53" y2="92" stroke="#0D2D0D" strokeWidth="2" strokeLinecap="round"/>
              <line x1="75" y1="78" x2="77" y2="92" stroke="#0D2D0D" strokeWidth="2" strokeLinecap="round"/>
              {/* Code badge on chest */}
              <rect x="50" y="85" width="30" height="22" rx="6" fill="#0D2D0D" stroke="#22C55E" strokeWidth="1.5"/>
              <text x="55" y="100" fontSize="10" fill="#22C55E" fontFamily="monospace" fontWeight="bold">&lt;/&gt;</text>
              {/* Arms */}
              <path d="M38 80 Q22 88 24 100" stroke="#1A4A1A" strokeWidth="12" strokeLinecap="round" fill="none"/>
              <path d="M92 80 Q108 88 106 100" stroke="#1A4A1A" strokeWidth="12" strokeLinecap="round" fill="none"/>
              {/* Hands */}
              <ellipse cx="24" cy="103" rx="7" ry="6" fill="#D4A574"/>
              <ellipse cx="106" cy="103" rx="7" ry="6" fill="#D4A574"/>
              {/* Neck */}
              <rect x="60" y="60" width="10" height="14" rx="4" fill="#D4A574"/>
              {/* Head */}
              <ellipse cx="65" cy="46" rx="22" ry="24" fill="#D4A574"/>
              {/* Hair */}
              <path d="M43 40 Q44 18 65 16 Q86 18 87 40 Q82 28 65 27 Q48 28 43 40Z" fill="#3D1F00"/>
              {/* Glasses */}
              <rect x="50" y="42" width="12" height="9" rx="3" stroke="#1A1A1A" strokeWidth="1.5" fill="rgba(100,200,255,0.15)"/>
              <rect x="68" y="42" width="12" height="9" rx="3" stroke="#1A1A1A" strokeWidth="1.5" fill="rgba(100,200,255,0.15)"/>
              <line x1="62" y1="46" x2="68" y2="46" stroke="#1A1A1A" strokeWidth="1.5"/>
              <line x1="44" y1="46" x2="50" y2="46" stroke="#1A1A1A" strokeWidth="1.5"/>
              <line x1="80" y1="46" x2="86" y2="46" stroke="#1A1A1A" strokeWidth="1.5"/>
              {/* Eyes */}
              <circle cx="56" cy="46" r="2.5" fill="#3D1F00"/>
              <circle cx="74" cy="46" r="2.5" fill="#3D1F00"/>
              {/* Smile */}
              <path d="M58 55 Q65 60 72 55" stroke="#C4845A" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            </svg>
          </div>

          {/* Text */}
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:26, fontWeight:900, color:'#fff', margin:'0 0 8px', lineHeight:1.3 }}>
              Build real solutions. Work with real clients.<br/>
              <span style={{ color:'#4ADE80' }}>Start your journey as a professional developer today!</span>
            </h2>
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:16, flexShrink:0, flexWrap:'wrap', justifyContent:'center' }}>
            <button onClick={() => setShowApply('enroll')} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'14px 34px', background:'#16A34A', color:'#fff', borderRadius:8, fontSize:15, fontWeight:700, border:'none', cursor:'pointer', boxShadow:'0 4px 18px rgba(22,163,74,0.4)', whiteSpace:'nowrap' }}>
              Apply Now
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <Link to="/contact" style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'14px 26px', background:'transparent', color:'#fff', border:'1.5px solid rgba(255,255,255,0.35)', borderRadius:8, fontSize:15, fontWeight:700, textDecoration:'none', whiteSpace:'nowrap' }}>
              Talk to an Advisor
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013 5.18a2 2 0 012-2.18h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L9.91 9.91a16 16 0 006.18 6.18l1.27-1.34a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
            </Link>
          </div>
        </div>
      </section>

      {showApply && (
        <RequestCallback
          isModal
          onClose={() => setShowApply(null)}
          onSuccess={handleApplySuccess}
          defaultProgram="Developer Full Time Program"
        />
      )}
    </div>
  )
}
